<template>
    <div class="collection">
        <draggable class="collection-items" v-model="$v.$model" :options="{handle: '.drag-handle'}">
            <div v-for="($vItem, index) in $v.$each.$iter" :key="index" class="collection-item mb-2" ref="items">
                <icon name="drag-horizontal" class="drag-handle" v-if="!disallowAll && !disallowSort"/>
                <span class="index">{{ parseInt(index)+1 }}</span>
                <btn theme="link" icon="delete" class="delete" @click="remove(index)" v-if="!disallowAll && !disallowDelete"/>

                <slot name="item" :$vItem="$vItem" :item="$vItem.$model" :is-new="false">
                    <text-area :value="getInputValue(index)"
                               @input="setInputValue(index, $event)"
                               @blur.native="setInputValue(index, getInputValue(index).trim())"
                               @paste.native="onPaste(index, $event)"
                               v-c:has-error="$vItem.$model instanceof Object ? $vItem[inputKey].$anyError : $vItem.$anyError"
                               :focus="inputKey"
                    />
                </slot>
                <slot name="item_extended" :$vItem="$vItem" :item="$vItem.$model"  :index="index"/>
            </div>
        </draggable>

        <div class="collection-item is-new mb-2" v-if="!disallowAll && !disallowAdd" ref="new_item" @focus.capture="focusNew">
            <btn theme="link" icon="plus" class="new-item" @click="addItem()"/>

            <slot name="new_item" :item="{}">
                <slot name="item" :is-new="true" :item="{}">
                    <text-area :focus="inputKey"/>
                </slot>
            </slot>
        </div>

        <slot name="after_items"/>

        <btn v-if="deleteAllLabel !== null && !disallowAll && !disallowDelete && $v.$model.length > 0"
             theme="none"
             class="mt-3 font-weight-bold"
             icon="delete"
             :label="deleteAllLabel"
             @click="removeAll"
        />
    </div>
</template>

<script>
    import draggable from 'vuedraggable';
    import cloneDeep from 'lodash/cloneDeep';

    export default {
        components: {draggable},

        props: {
            $v: Object,
            inputKey: {type: String, default: null},
            disallowAll: {type: Boolean, default: false},
            disallowAdd: {type: Boolean, default: false},
            disallowDelete: {type: Boolean, default: false},
            disallowSort: {type: Boolean, default: false},
            deleteAllLabel: {type: String, default: null},
            newItem: {required: true},
        },

        methods: {
            remove(index) {
                this.$delete(this.$v.$model, index);
            },

            removeAll() {
                this.$set(this.$v, '$model', []);
                this.$v.$reset();
            },

            getInputValue(index) {
                let value = this.$v.$model[index];
                if (value instanceof Object) {
                    value = value[this.inputKey];
                }
                return value || '';
            },

            setInputValue(index, value) {
                if (this.$v.$model[index] instanceof Object) {
                    this.$set(this.$v.$model[index], this.inputKey, value);
                } else {
                    this.$set(this.$v.$model, index, value);
                }
            },

            focusNew(ev) {
                if (ev.target instanceof Node && ev.target.hasAttribute('focus')) {
                    this.addItem(ev.target.getAttribute('focus'));
                }
            },

            async onPaste(index, ev) {
                index = parseInt(index);

                await this.$nextTick(); // target value is only set after nextTick

                if (index+1 !== this.$v.$model.length || this.disallowAdd || this.disallowAll || ev.target.value.match(/\n/) === null) {
                    // Only allow multi line past feature for last element in collection and if adding new elements is
                    // allowed. Also go out if pasted value does not contain line breaks.
                    return;
                }

                // delete last item, it contains the pasted input without line breaks
                this.$v.$model.splice(index, 1);

                let buggylength = this.$v.$model.length;
                await this.$nextTick(); // wait for delete in dom

                if (buggylength !== this.$v.$model.length) {
                    // something pushes an empty string to $v.$model, if this happens we delete it here
                    this.$v.$model.splice(index, 1);
                    await this.$nextTick();
                }
                index--;

                // add new items
                let values = ev.target.value.split(/\n/);
                for (let value of values) {
                    let item = cloneDeep(this.newItem);
                    item[this.inputKey] = value.replace(/^\s*(.*?)\s*$/gi, '$1');
                    this.$v.$model.push(item);
                    index++;
                }

                await this.$nextTick(); // wait until all new items are rendered to dom

                let el = this.$refs.items[index].querySelector(`[focus]`);
                if (el) {
                    el.focus();
                }
            },

            async addItem(focus = null) {
                let index = this.$v.$model.push(cloneDeep(this.newItem)) - 1;

                await this.$nextTick(); // wait new item rendered to dom

                let el;
                if (focus === null) {
                    el = this.$refs.items[index].querySelector(`[focus]`);
                } else {
                    el = this.$refs.items[index].querySelector(`[focus="${focus}"]`);
                }
                if (el) {
                    el.focus();
                }
            },
        },
    };
</script>